import type {
  BoxProps,
  IconButtonProps,
  MotionPropsFor,
} from '@hover/blueprint';
import { Body, Box, IconButton, motion } from '@hover/blueprint';
import type { IconProp } from '@hover/icons';
import { iconArrowLeft, iconArrowRight } from '@hover/icons';
import type { TFunction } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { FullscreenImageThumbnail } from 'src/modules/homeownerApp/components/FullscreenImage';
import { Image } from 'src/modules/homeownerApp/components/SharePageLayout/Image';
import {
  useHomeownerAppContext,
  useHomeownerTranslate,
} from 'src/modules/homeownerApp/hooks';
import type { AiDisplayImage } from 'src/shared/graphql/generated';
import { generatePath } from 'src/shared/route';
import { imageLoaded } from 'src/shared/telemetry';

export const animationVariants = {
  hide: {
    x: '-100%',
    transition: { duration: 0.5, ease: 'easeInOut' },
  },
  show: {
    x: '0%',
    transition: { duration: 0.5, ease: 'easeInOut' },
  },
};

interface CarouselArrowProps extends MotionPropsFor<IconButtonProps> {
  icon: IconProp;
}

const MotionBox = motion(Box);
const MotionButton = motion(IconButton);

const CarouselArrow = ({ icon, ...props }: CarouselArrowProps) => (
  <MotionButton
    colorScheme="light"
    icon={icon}
    initial={{ opacity: 0 }}
    justifyContent="space-between"
    position="absolute"
    top="50%"
    transform={{ translateY: '-50%' }}
    variant="glass"
    {...props}
  />
);

export interface ImagesProps extends BoxProps {
  aiDisplayImages: AiDisplayImage[];
  orgId?: number | null;
  visibleIndex: number;
  fullScreenPath: string;
  setVisibleIndex: React.Dispatch<React.SetStateAction<number>>;
}

export const getAiGeneratedImageStyle = (t: TFunction, style: string = '') =>
  style === t('share.myUpload') ? t('share.edited') : style;

export const Images = ({
  aiDisplayImages,
  orgId,
  visibleIndex,
  setVisibleIndex,
  fullScreenPath,
  ...props
}: ImagesProps) => {
  const t = useHomeownerTranslate();

  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [isHovered, setIsHovered] = useState(false);

  const { trackingData } = useHomeownerAppContext();

  useEffect(() => {
    if (aiDisplayImages?.length) {
      imageLoaded({
        ...trackingData,
        ai_original_image_id:
          aiDisplayImages[visibleIndex]?.aiOriginalImage?.id,
        ai_generated_image_id:
          aiDisplayImages[visibleIndex]?.aiGeneratedImage?.id,
        feature: 'lead-landing-page',
        page_or_screen_name: 'lead-landing-page',
        track_id: 'lead-landing-image-viewed',
        org_id: orgId ?? undefined,
        ai_image_watermarked: true,
      });
    }
  }, [aiDisplayImages, orgId, trackingData, visibleIndex]);

  if (!aiDisplayImages) return null;

  const displayPrev = visibleIndex > 0;
  const displayNext = visibleIndex < aiDisplayImages.length - 1;

  return (
    <Box flexDirection="column" {...props}>
      <Box flexDirection="column" position="relative">
        <Box
          borderRadius={400}
          display="inline-block"
          flexDirection="row"
          overflow="hidden"
          position="relative"
          zIndex={0}
        >
          {aiDisplayImages.map((image, index) => {
            const show = index >= visibleIndex;

            return (
              <MotionBox
                alignItems="center"
                animate={show ? 'show' : 'hide'}
                color="neutral.500"
                display="flex"
                initial="show"
                justifyContent="center"
                key={image.id}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                position={
                  aiDisplayImages.length - 1 > index ? 'absolute' : 'inherit'
                }
                top={0}
                variants={animationVariants}
                zIndex={aiDisplayImages.length - index}
              >
                <FullscreenImageThumbnail
                  image={image}
                  onClick={() => {
                    history.push(
                      generatePath(fullScreenPath, {
                        id,
                        aiDisplayImageId: image.id,
                      }),
                    );
                  }}
                >
                  <Image cursor="pointer" image={image} />
                </FullscreenImageThumbnail>
              </MotionBox>
            );
          })}
        </Box>
        <CarouselArrow
          animate={{ opacity: isHovered && displayPrev ? 1 : 0 }}
          icon={iconArrowLeft}
          label={t('fullscreenImage.previous')}
          left="20px"
          onClick={() => setVisibleIndex(x => Math.max(0, x - 1))}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          whileHover={{ opacity: displayPrev ? 1 : 0 }}
        />
        <CarouselArrow
          animate={{ opacity: isHovered && displayNext ? 1 : 0 }}
          icon={iconArrowRight}
          label={t('fullscreenImage.next')}
          onClick={() =>
            setVisibleIndex(x => Math.min(aiDisplayImages.length - 1, x + 1))
          }
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          right="20px"
          whileHover={{ opacity: displayNext ? 1 : 0 }}
        />
        <Body
          backdropFilter="blur(25px)"
          background="darkAlpha.300"
          borderRadius="round"
          bottom={500}
          color="neutral.0"
          isBold
          left={500}
          margin={0}
          paddingX={400}
          paddingY={200}
          position="absolute"
          size={200}
          withCapsize
        >
          {aiDisplayImages.length &&
            `${visibleIndex + 1} of ${aiDisplayImages.length}`}
        </Body>
      </Box>
      <Body fontWeight={700}>
        {getAiGeneratedImageStyle(
          t,
          aiDisplayImages[visibleIndex]?.aiGeneratedImage.style,
        )}
      </Body>
    </Box>
  );
};
